import { useSearchParams } from 'react-router-dom';
import type { DataTableSort } from '@bcecloud/portal-ui';

/**
 * URL-backed column sort state for a list page (`?sort=<key>&dir=<asc|desc>`),
 * so a sorted view survives reload and can be sent to a colleague.
 *
 * Clicking the active column flips its direction; clicking a new column sorts
 * ascending. Changing sort resets pagination to page 1.
 *
 * @param allowedKeys - The sortable keys — anything else in the URL is ignored.
 * @param defaultSort - Ordering to apply when the URL names none. Shown as the
 *   active column, so the list never claims to be unsorted while it is.
 * @param options - `prefix` gives a second table on the same page its own
 *   params (`?<prefix>_sort=&<prefix>_dir=`), so sorting one leaves the other alone.
 * @returns The active sort (if any) and the header click handler.
 */
export function useTableSort(
  allowedKeys: readonly string[],
  defaultSort?: DataTableSort,
  options: { readonly prefix?: string } = {},
): {
  readonly sort: DataTableSort | undefined;
  readonly onSort: (key: string) => void;
} {
  const [params, setParams] = useSearchParams();
  const sortParam = options.prefix ? `${options.prefix}_sort` : 'sort';
  const dirParam = options.prefix ? `${options.prefix}_dir` : 'dir';
  const key = params.get(sortParam);
  const direction = params.get(dirParam) === 'desc' ? 'desc' : 'asc';
  const sort =
    key !== null && allowedKeys.includes(key)
      ? ({ key, direction } as DataTableSort)
      : defaultSort;

  function onSort(nextKey: string): void {
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        const flipped = sort?.key === nextKey && sort.direction === 'asc' ? 'desc' : 'asc';
        next.set(sortParam, nextKey);
        next.set(dirParam, sort?.key === nextKey ? flipped : 'asc');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  }

  return { sort, onSort };
}
